import { collection, doc, getDocs, runTransaction, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { fbCollectionPath, fbList } from "../hooks/useFirebase";
import { getUser } from "./sessao";
import { calcPagamento } from "./pagamento";
import { custoUnitarioProduto } from "./calcCusto";
import { montarRepasse, pagoNoCaixa } from "./vendaOnline";
import { normalizarStatus, statusDoOnline, statusParaOnline, pedidoOnlineOculto } from "../constants/statusPedido";

const moeda = valor => Math.round((Number(valor) || 0) * 100) / 100;
const numero = valor => Math.max(0, Number(valor) || 0);
const referencia = (colecao, id) => doc(db, fbCollectionPath(colecao), id);
const historico = pedido => Array.isArray(pedido.historico) ? pedido.historico : [];

// Mapas do Firestore podem chegar com chaves em outra ordem no cache e no
// servidor. A ordem das chaves não é uma alteração; a dos itens de arrays é.
const assinatura = valor => JSON.stringify(valor ?? null, (_chave, v) =>
  v && typeof v === "object" && !Array.isArray(v)
    ? Object.fromEntries(Object.keys(v).sort().map(chave => [chave, v[chave]]))
    : v);
const igual = (a, b) => assinatura(a) === assinatura(b);
const valorComparavel = (pedido, campo) => {
  if (["itens", "pagamentos", "materiais_usados", "filamentos_usados"].includes(campo)) return pedido[campo] || [];
  if (campo === "status") return normalizarStatus(pedido.status);
  if (campo === "valor") return moeda(pedido.valor);
  return pedido[campo];
};
export async function salvarAlteracoesPedido(pedido, patch) {
  usuario();
  const ref = referencia("pedidos", pedido.id);
  return runTransaction(db, async tx => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error("Este pedido não está mais disponível.");
    const atual = snap.data();
    // Compare conteúdo, não timestamps de sincronização. Inclua também os
    // campos editados (cliente, observação, prazo...), evitando sobrescrevê-los.
    const campos = [...new Set(["status", "valor", "itens", "venda_online", "pagamentos", "sinal_pago", "materiais_usados", ...Object.keys(patch)])]
      .filter(c => !["updated_at", "created_at", "data"].includes(c));
    if (campos.some(c => !igual(valorComparavel(atual, c), valorComparavel(pedido, c)))) {
      const erro = new Error("O pedido foi atualizado enquanto você editava. Confira a versão atual antes de salvar.");
      erro.code = "pedido/desatualizado";
      erro.pedidoAtual = { ...atual, id: pedido.id };
      throw erro;
    }
    const mudouItens = patch.itens && !igual(patch.itens, atual.itens || []);
    const mudouMateriais = patch.materiais_usados && !igual(patch.materiais_usados, atual.materiais_usados || []);
    const mudouFinanceiro = (patch.valor != null && Math.abs(Number(patch.valor) - Number(atual.valor)) > 0.005) || ("venda_online" in patch && !igual(patch.venda_online, atual.venda_online));
    if ((mudouItens || mudouMateriais) && (atual.estoque_baixado || atual.estoque_produtos_baixado || atual.estoque_producao_baixado)) throw new Error("O estoque deste pedido já foi movimentado. Confira e cancele a produção antes de trocar produtos ou materiais.");
    if ((mudouItens || mudouFinanceiro) && atual.pedidoOnlineId) throw new Error("Os produtos e valores deste pedido estão vinculados à compra no site e não podem ser alterados aqui.");
    if ((mudouItens || mudouFinanceiro) && (calcPagamento(atual).recebido > 0 || atual.venda_online?.repasse_em)) throw new Error("Estorne os recebimentos antes de alterar os produtos ou valores deste pedido.");
    if ((mudouItens || mudouFinanceiro) && atual.cupom) throw new Error("Este pedido possui desconto de cupom. Preserve seus valores e crie um novo pedido para refazer a venda.");
    const { status, created_at, data, ...dados } = patch;
    tx.update(ref, { ...dados, updated_at: serverTimestamp() });
  });
}

export function podeExcluirPedido(p) {
  return !!p && !(p.pedidoOnlineId || p.estoque_baixado || p.estoque_produtos_baixado || p.estoque_producao_baixado || p.historico?.length || calcPagamento(p).pago > 0)
    && ["aguardando_impressao", "conferindo_pagamento"].includes(normalizarStatus(p.status));
}

export async function excluirPedidoRascunho(pedido) {
  usuario();
  const ref = referencia("pedidos", pedido.id);
  return runTransaction(db, async tx => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return;
    const p = snap.data();
    if (!podeExcluirPedido(p)) {
      throw new Error("Este pedido já tem movimentações. Use Cancelar pedido e, se necessário, estorne o recebimento para preservar o histórico.");
    }
    tx.delete(ref);
  });
}

export async function registrarPerdaPedido(pedido, dados) {
  const user = usuario();
  const fila = (await fbList("fila_impressao")).filter(f => f.pedidoId === pedido.id && !f.tentativa_encerrada && !["concluido", "falhou"].includes(f.status));
  const pedidoRef = referencia("pedidos", pedido.id), falhaRef = doc(collection(db, fbCollectionPath("falhas")));
  const em = new Date().toISOString(), por = user.nome || "—";
  return runTransaction(db, async tx => {
    const snap = await tx.get(pedidoRef);
    if (!snap.exists()) throw new Error("Este pedido não está mais disponível.");
    const atual = snap.data();
    if (normalizarStatus(atual.status) !== "imprimindo") throw new Error("Registre a falha enquanto o pedido estiver em impressão.");
    const filas = await Promise.all(fila.map(async f => { const ref = referencia("fila_impressao", f.id); return { ref, snap: await tx.get(ref) }; }));
    const onlineRef = atual.pedidoOnlineId && atual.vendedorOnlineId ? doc(db, "users", atual.vendedorOnlineId, "pedidos_online", atual.pedidoOnlineId) : null;
    const onlineSnap = onlineRef ? await tx.get(onlineRef) : null;
    tx.set(falhaRef, { ...dados, pedidoId: pedido.id, nome: atual.produto || "Pedido", cliente: atual.cliente || null, impressora: atual.impressora_nome || null, data: em, por });
    // O material da tentativa que falhou foi perdido, e não volta ao estoque.
    // A próxima tentativa fará seu próprio consumo. Produtos reservados ficam.
    tx.update(pedidoRef, { status: "aguardando_impressao", estoque_baixado: false, estoque_produtos_baixado: !!atual.estoque_produtos_baixado || (!!atual.estoque_baixado && atual.estoque_producao_baixado == null), estoque_producao_baixado: false, estoque_movimentos: (atual.estoque_movimentos || []).filter(m => m.col === "produtos"), updated_at: serverTimestamp(), historico: [...historico(atual), { tipo: "falha", motivo: dados.motivo, em, por }] });
    for (const f of filas) if (f.snap.exists()) tx.update(f.ref, { status: "falhou", tentativa_encerrada: true });
    if (onlineSnap?.exists()) tx.update(onlineRef, { status: "pago", updated_at: Date.now(), naoLidoCliente: true });
  });
}

function usuario() {
  const user = getUser();
  if (!user?.uid) throw new Error("Entre novamente na sua conta.");
  return user;
}

function conferirFinanceiro(atual, esperado) {
  if (atual.status === "cancelado") throw new Error("O pedido está cancelado. Reabra o pedido antes de receber.");
  if (moeda(atual.valor) !== moeda(esperado.valor)
    || !igual(atual.pagamentos || [], esperado.pagamentos || [])
    || atual.sinal_pago !== esperado.sinal_pago
    || !igual(atual.venda_online, esperado.venda_online)) {
    throw new Error("O pedido mudou. Feche e abra novamente para conferir os valores.");
  }
}

// Um sinal antigo continua compondo o recebido quando o primeiro pagamento
// moderno é adicionado. O saldo histórico não ganha uma nova entrada no caixa.
function pagamentosAtuais(pedido) {
  if (Array.isArray(pedido.pagamentos)) return [...pedido.pagamentos];
  const valor = moeda(calcPagamento(pedido).recebido);
  return valor > 0 ? [{ id: "saldo-legado", valor, metodo: "Pagamento anterior", legado: true }] : [];
}

function lancamento(pedido, valor, descricao) {
  return { descricao, valor, tipo: valor < 0 ? "Saída" : "Entrada", origem: "pedido", pedido_id: pedido.id, created_at: serverTimestamp() };
}

async function lerPedido(tx, ref, esperado) {
  const snap = await tx.get(ref);
  if (!snap.exists()) throw new Error("Este pedido não está mais disponível.");
  const atual = { ...snap.data(), id: esperado.id };
  conferirFinanceiro(atual, esperado);
  return atual;
}

export async function registrarPagamentoPedido(pedido, valorReceber, metodo = "manual") {
  const user = usuario();
  const valor = moeda(valorReceber);
  if (!Number.isFinite(Number(valorReceber)) || valor <= 0) throw new Error("Informe um valor maior que zero.");
  const pedidoRef = referencia("pedidos", pedido.id);
  const caixaRef = doc(collection(db, fbCollectionPath("caixa")));
  const em = new Date().toISOString(), por = user.nome || "—";
  return runTransaction(db, async tx => {
    const atual = await lerPedido(tx, pedidoRef, pedido);
    const saldo = calcPagamento(atual).saldo;
    if (valor > saldo + 0.005) throw new Error("O valor informado é maior que o saldo do pedido.");
    const pag = { id: caixaRef.id, valor, metodo, em, por };
    tx.update(pedidoRef, { pagamentos: [...pagamentosAtuais(atual), pag], historico: [...historico(atual), { tipo: "pagamento", valor, metodo, em, por }] });
    tx.set(caixaRef, lancamento(atual, valor, `Pagamento: ${atual.produto || atual.cliente || "pedido"}`));
    return { valor };
  });
}

export async function estornarPagamentosPedido(pedido) {
  const user = usuario();
  const pedidoRef = referencia("pedidos", pedido.id);
  const caixaRef = doc(collection(db, fbCollectionPath("caixa")));
  const em = new Date().toISOString(), por = user.nome || "—";
  return runTransaction(db, async tx => {
    const snap = await tx.get(pedidoRef);
    if (!snap.exists()) throw new Error("Este pedido não está mais disponível.");
    const atual = { ...snap.data(), id: pedido.id };
    // Estorno continua disponível em pedidos cancelados.
    conferirFinanceiro({ ...atual, status: "estorno" }, pedido);
    const pg = calcPagamento(atual);
    if (pg.pago <= 0) return { valor: 0 };
    const valor = pagoNoCaixa(atual, pg.pago);
    const patch = { pagamentos: [], sinal_pago: null, historico: [...historico(atual), { tipo: "estorno", valor, em, por }] };
    if (atual.venda_online?.repasse_em) patch.venda_online = { ...atual.venda_online, repasse_em: null };
    tx.update(pedidoRef, patch);
    if (valor > 0) tx.set(caixaRef, lancamento(atual, -valor, `Estorno: ${atual.produto || atual.cliente || "pedido"}`));
    return { valor };
  });
}

export async function confirmarRepassePedido(pedido, valorRecebido, lancarCustos = false) {
  const user = usuario();
  if (!Number.isFinite(Number(valorRecebido)) || Number(valorRecebido) < 0) throw new Error("Informe um valor de repasse válido.");
  const pedidoRef = referencia("pedidos", pedido.id);
  const caixaRef = doc(collection(db, fbCollectionPath("caixa")));
  const custoRef = referencia("caixa", `${caixaRef.id}_custos`);
  const em = new Date().toISOString(), por = user.nome || "—";
  return runTransaction(db, async tx => {
    const atual = await lerPedido(tx, pedidoRef, pedido);
    if (moeda(valorRecebido) > calcPagamento(atual).saldo + 0.005) throw new Error("O repasse informado é maior que o saldo do pedido.");
    const base = pagamentosAtuais(atual);
    const r = montarRepasse({ ...atual, pagamentos: base }, valorRecebido, { por, em, id: caixaRef.id, lancarCustos });
    if (!r) return null;
    const canal = atual.venda_online?.canal_nome || "loja";
    const retido = moeda(r.pagamentos.filter(p => p.retido).reduce((s, p) => s + p.valor, 0));
    tx.update(pedidoRef, { pagamentos: [...base, ...r.pagamentos], venda_online: r.vendaOnline, historico: [...historico(atual), { tipo: "repasse", valor: r.entradaCaixa, retido, canal, em, por }] });
    if (r.entradaCaixa > 0) tx.set(caixaRef, lancamento(atual, r.entradaCaixa, `Repasse ${canal}: ${atual.produto || atual.cliente || "pedido"}`));
    if (r.saidaCaixa > 0) tx.set(custoRef, lancamento(atual, -r.saidaCaixa, `Custos da venda ${canal}: ${atual.produto || atual.cliente || "pedido"}`));
    return r;
  });
}

function dataPedido(po) {
  const raw = po.created_at || po.ts || po.updated_at;
  const data = raw?.toDate ? raw.toDate() : raw?.seconds ? new Date(raw.seconds * 1000) : new Date(raw || Date.now());
  return Number.isNaN(data.getTime()) ? new Date() : data;
}

// O vínculo no documento da loja arbitra concorrência entre sessões. Pedidos
// já espelhados conservam seu ID; apenas pedidos novos ganham um ID estável.
export async function espelharPedidosSite(lista = [], { produtos = [], impressoras = [] } = {}) {
  const user = usuario();
  const snap = await getDocs(collection(db, "users", user.uid, "pedidos_online"));
  const existentes = new Map(lista.filter(p => p.pedidoOnlineId).map(p => [p.pedidoOnlineId, p.id]));
  for (const onlineDoc of snap.docs) {
    const onlineRef = doc(db, "users", user.uid, "pedidos_online", onlineDoc.id);
    await runTransaction(db, async tx => {
      const onlineSnap = await tx.get(onlineRef);
      if (!onlineSnap.exists()) return;
      const po = onlineSnap.data();
      // Checkout do Mercado Pago ainda não pago (ou desistido): não é pedido
      if (pedidoOnlineOculto(po)) return;
      const id = po.pedidoGestaoId || existentes.get(onlineDoc.id) || `site_${user.uid}_${onlineDoc.id}`;
      const pedidoRef = referencia("pedidos", id);
      const pedidoSnap = await tx.get(pedidoRef);
      if (pedidoSnap.exists()) {
        const atual = pedidoSnap.data();
        const patch = {};
        const status = statusDoOnline(po.status, atual.status);
        if (normalizarStatus(atual.status) !== status) patch.status = status;
        if (po.cupom?.codigo && !atual.cupom?.codigo) Object.assign(patch, { cupom: po.cupom, desconto: po.desconto || 0, subtotal: po.subtotal ?? po.total ?? 0, valor: po.total ?? atual.valor });
        if (po.estoque_baixado && !atual.estoque_produtos_baixado) patch.estoque_produtos_baixado = true;
        // Pagamento confirmado pelo backend do Mercado Pago depois que a cópia já existia
        const atuais = Array.isArray(atual.pagamentos) ? atual.pagamentos : [];
        const confirmados = (Array.isArray(po.pagamentos) ? po.pagamentos : []).filter(p => p?.mp?.verificado && !atuais.some(x => x?.id === p.id));
        if (confirmados.length) patch.pagamentos = [...atuais, ...confirmados];
        if (Object.keys(patch).length) tx.update(pedidoRef, patch);
      } else {
        const itens = (po.itens || []).map(it => {
          const prod = produtos.find(p => p.id === (it.produto_id || it.id));
          return {
            ...(prod ? { custo_unit: custoUnitarioProduto(prod, impressoras), filamentos_usados: prod.filamentos_usados || [], materiais_usados: prod.materiais_usados || [], horas_impressao: prod.horas_impressao || null, impressora_id: prod.impressora_id || null } : {}),
            ...it, produto_id: it.produto_id || it.id || null, nome: it.nome || "Produto", qtd: numero(it.qtd) || 1, valor_unit: numero(it.valor_unit ?? it.preco),
          };
        });
        const status = statusDoOnline(po.status);
        tx.set(pedidoRef, {
          cliente: po.cliente?.nome || "Cliente do site", cliente_celular: po.cliente?.celular || null,
          cliente_email: po.cliente?.email || null, cliente_wa: !!po.cliente?.celular,
          produto: itens[0]?.nome || "Pedido do site", itens,
          valor: po.total ?? po.subtotal ?? 0, subtotal: po.subtotal ?? po.total ?? 0,
          desconto: po.desconto || 0, cupom: po.cupom || null, status, origem: "site",
          pedidoOnlineId: onlineDoc.id, vendedorOnlineId: user.uid, pagamento: po.pagamento || null,
          ...(Array.isArray(po.pagamentos) ? { pagamentos: po.pagamentos } : status === "entregue" ? {} : { pagamentos: [] }),
          estoque_baixado: !!po.estoque_baixado, estoque_produtos_baixado: !!po.estoque_baixado,
          estoque_producao_baixado: false,
          data: dataPedido(po).toLocaleDateString("pt-BR"), created_at: po.created_at || dataPedido(po).toISOString(),
        });
      }
      if (po.pedidoGestaoId !== id) tx.update(onlineRef, { pedidoGestaoId: id });
    });
  }
  return fbList("pedidos");
}

const CONFIRMADOS_ONLINE = ["pago", "imprimindo", "pronto", "em_entrega", "concluido"];
const CAMPOS_ESTOQUE = { produtos: "estoque", filamentos: "peso_restante", materiais: "quantidade" };

// Registra a quantidade realmente retirada. O sistema antigo truncava em
// zero, portanto seus movimentos não podem ser reconstruídos com segurança.
async function planejarEstoque(tx, pedido, online, { baixarProdutos = false, baixarProducao = false, cancelar = false } = {}) {
  const movimentosPedido = Array.isArray(pedido?.estoque_movimentos) ? pedido.estoque_movimentos : [];
  const movimentosOnline = Array.isArray(online?.estoque_movimentos) ? online.estoque_movimentos : [];
  const pedidoPatch = {}, onlinePatch = {}, avisos = [], deltas = new Map();
  const legadoBaixado = !!pedido?.estoque_baixado && pedido?.estoque_producao_baixado == null;
  const produtosBaixados = !!pedido?.estoque_produtos_baixado || !!online?.estoque_baixado || legadoBaixado;
  const producaoBaixada = !!pedido?.estoque_producao_baixado || legadoBaixado;
  const add = (col, id, qtd) => {
    if (!id || !CAMPOS_ESTOQUE[col] || !(qtd > 0)) return;
    const key = `${col}/${id}`;
    const anterior = deltas.get(key);
    deltas.set(key, { col, id, campo: CAMPOS_ESTOQUE[col], quantidade: (anterior?.quantidade || 0) + qtd });
  };
  if (cancelar) {
    [...movimentosPedido, ...movimentosOnline].forEach(m => add(m.col, m.id, numero(m.quantidade)));
    if ((produtosBaixados || producaoBaixada) && !deltas.size) avisos.push("Este pedido tem baixa antiga sem movimentos detalhados. Confira a devolução do estoque manualmente.");
    else if (online?.estoque_baixado && !movimentosOnline.length && !movimentosPedido.some(m => m.col === "produtos")) avisos.push("A baixa antiga dos produtos da loja precisa ser conferida manualmente.");
    Object.assign(pedidoPatch, { estoque_baixado: false, estoque_produtos_baixado: false, estoque_producao_baixado: false, estoque_movimentos: [] });
    Object.assign(onlinePatch, { estoque_baixado: false, estoque_movimentos: [] });
  } else {
    if (baixarProdutos && !produtosBaixados) {
      const itens = pedido?.itens?.length ? pedido.itens : online?.itens || [];
      itens.forEach(it => { if (!it.encomenda) add("produtos", it.produto_id || it.id, numero(it.qtd) || 1); });
      if (!itens.length && pedido?.produto_id) add("produtos", pedido.produto_id, 1);
      pedidoPatch.estoque_produtos_baixado = true;
      onlinePatch.estoque_baixado = true;
    }
    if (baixarProducao && !producaoBaixada && pedido) {
      (pedido.filamentos_usados || []).forEach(f => add("filamentos", f.id, numero(f.peso)));
      (pedido.materiais_usados || []).forEach(m => add("materiais", m.id, numero(m.qtd)));
      (pedido.itens || []).forEach(it => {
        const qtd = numero(it.qtd) || 1;
        // Produto de pronta-entrega vindo do site já existe fisicamente.
        if (online && it.encomenda === false) return;
        (it.filamentos_usados || []).forEach(f => add("filamentos", f.id, numero(f.peso) * qtd));
        (it.materiais_usados || []).forEach(m => add("materiais", m.id, numero(m.qtd) * qtd));
      });
      pedidoPatch.estoque_producao_baixado = true;
      pedidoPatch.estoque_baixado = true;
    }
  }
  const updates = [], novosMovimentos = [];
  // Todas as leituras acontecem antes das escritas da transação.
  const recursos = await Promise.all([...deltas.values()].map(async m => {
    const ref = referencia(m.col, m.id);
    return { ...m, ref, snap: await tx.get(ref) };
  }));
  for (const m of recursos) {
    if (!m.snap.exists()) { avisos.push("Um material ou produto deste pedido não está mais no catálogo; confira o estoque."); continue; }
    const atual = numero(m.snap.data()[m.campo]);
    const quantidade = cancelar ? m.quantidade : Math.min(atual, m.quantidade);
    if (!cancelar && quantidade < m.quantidade) avisos.push("O estoque era menor que o consumo informado. Foi registrada apenas a quantidade disponível; confira a reposição.");
    if (quantidade > 0) {
      updates.push({ ref: m.ref, patch: { [m.campo]: Number((atual + (cancelar ? quantidade : -quantidade)).toFixed(6)) } });
      novosMovimentos.push({ col: m.col, id: m.id, campo: m.campo, quantidade });
    }
  }
  if (!cancelar && novosMovimentos.length) {
    if (pedido) pedidoPatch.estoque_movimentos = [...movimentosPedido, ...novosMovimentos];
    else onlinePatch.estoque_movimentos = [...movimentosOnline, ...novosMovimentos];
  }
  return { updates, pedidoPatch, onlinePatch, avisos: [...new Set(avisos)] };
}

export async function mudarStatusPedido(pedido, novoStatus) {
  const user = usuario();
  const status = normalizarStatus(novoStatus);
  const pedidoRef = referencia("pedidos", pedido.id);
  const em = new Date().toISOString(), por = user.nome || "—";
  return runTransaction(db, async tx => {
    const snap = await tx.get(pedidoRef);
    if (!snap.exists()) throw new Error("Este pedido não está mais disponível.");
    const atual = { ...snap.data(), id: pedido.id };
    const de = normalizarStatus(atual.status);
    if (de === status) return { alterado: false, avisos: [] };
    if (de !== normalizarStatus(pedido.status)) throw new Error("O status do pedido mudou. Confira a etapa atual antes de continuar.");
    const onlineRef = atual.pedidoOnlineId && atual.vendedorOnlineId ? doc(db, "users", atual.vendedorOnlineId, "pedidos_online", atual.pedidoOnlineId) : null;
    const onlineSnap = onlineRef ? await tx.get(onlineRef) : null;
    const online = onlineSnap?.exists() ? onlineSnap.data() : null;
    const plano = await planejarEstoque(tx, atual, online, { baixarProdutos: status === "imprimindo", baixarProducao: status === "imprimindo", cancelar: status === "cancelado" });
    for (const m of plano.updates) tx.update(m.ref, m.patch);
    tx.update(pedidoRef, { ...plano.pedidoPatch, status, updated_at: serverTimestamp(), historico: [...historico(atual), { tipo: "status", de, para: status, em, por }] });
    if (online) tx.update(onlineRef, { ...plano.onlinePatch, status: statusParaOnline(status), pedidoGestaoId: pedido.id, updated_at: Date.now(), naoLidoCliente: true });
    return { alterado: true, avisos: plano.avisos };
  });
}

export async function mudarStatusPedidoOnline(vendedorId, pedido, status) {
  const user = usuario();
  if (user.uid !== vendedorId) throw new Error("Esta loja não pertence à sessão atual.");
  // Localiza espelhos legados antes da transação. O vínculo gravado no pedido
  // online prevalece e os novos espelhos sempre possuem ID determinístico.
  const lista = await fbList("pedidos");
  const legado = lista.find(p => p.pedidoOnlineId === pedido.id);
  const onlineRef = doc(db, "users", vendedorId, "pedidos_online", pedido.id);
  const em = new Date().toISOString(), por = user.nome || "—";
  return runTransaction(db, async tx => {
    const onlineSnap = await tx.get(onlineRef);
    if (!onlineSnap.exists()) throw new Error("Este pedido não está mais disponível.");
    const online = onlineSnap.data();
    if (online.status === status) return { alterado: false, avisos: [] };
    if (online.status !== pedido.status) throw new Error("O status do pedido mudou. Confira a etapa atual antes de continuar.");
    const pedidoId = online.pedidoGestaoId || legado?.id || `site_${vendedorId}_${pedido.id}`;
    const pedidoRef = referencia("pedidos", pedidoId);
    const pedidoSnap = await tx.get(pedidoRef);
    const gestao = pedidoSnap.exists() ? { ...pedidoSnap.data(), id: pedidoId } : null;
    const plano = await planejarEstoque(tx, gestao, online, { baixarProdutos: CONFIRMADOS_ONLINE.includes(status), baixarProducao: status === "imprimindo", cancelar: status === "cancelado" });
    for (const m of plano.updates) tx.update(m.ref, m.patch);
    tx.update(onlineRef, { ...plano.onlinePatch, status, updated_at: Date.now(), naoLidoCliente: true, ...(gestao ? { pedidoGestaoId: pedidoId } : {}) });
    if (gestao) {
      const proximo = statusDoOnline(status, gestao.status);
      tx.update(pedidoRef, { ...plano.pedidoPatch, status: proximo, updated_at: serverTimestamp(), historico: [...historico(gestao), { tipo: "status", de: normalizarStatus(gestao.status), para: proximo, em, por }] });
    }
    return { alterado: true, avisos: plano.avisos };
  });
}
